// frontend/src/context/ListDetailFlipContext.tsx
// <purpose>Per-user preference: whether selecting a ticket widens the detail pane (2:1→1:2 morph) in AntwortenSeite. Default OFF (static split).</purpose>
// <constraints>FE-only: persisted in localStorage 'stx-listdetail-flip'. Default 'off'. No backend. ArchivSeite keeps morph always-on — this context is Antworten-only.</constraints>
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const KEY = 'stx-listdetail-flip';

interface Ctx { flip: boolean; setFlip: (b: boolean) => void; toggle: () => void; }
const ListDetailFlipCtx = createContext<Ctx>({ flip: false, setFlip: () => {}, toggle: () => {} });

function readStored(): boolean {
  try { return localStorage.getItem(KEY) === 'on'; } catch { return false; }
}

export const ListDetailFlipProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [flip, setFlipState] = useState<boolean>(readStored);
  const setFlip = useCallback((b: boolean) => {
    setFlipState(b);
    try { localStorage.setItem(KEY, b ? 'on' : 'off'); } catch { /* private mode / quota — preference is best-effort */ }
  }, []);
  // Re-reads storage (not the captured `flip`) so the stable callback always flips the
  // current value — capturing `flip` would freeze it at first-render.
  const toggle = useCallback(() => setFlip(!readStored()), [setFlip]);
  const value = useMemo(() => ({ flip, setFlip, toggle }), [flip, setFlip, toggle]);
  return <ListDetailFlipCtx.Provider value={value}>{children}</ListDetailFlipCtx.Provider>;
};

export function useListDetailFlip(): Ctx { return useContext(ListDetailFlipCtx); }
